import { FC, useContext, useState } from 'react';
import { Button, Icon, useToast, Alert } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../../../../../Localization';
import { SupportCaseInfoResponse } from '../../../types';
import { isPhoneContact } from '../../../utils';
import InterfaceContext, { TwilioDeviceContext, LoggedInConfigurationContext, PublicConfigurationContext } from '../../../../../Context';
import { useCallManager } from '../../shared/hooks';
import { CallConfirmationModal } from './components/CallConfirmationModal';
import { ActiveCallView } from './components/ActiveCallView';
import { PhoneKeypadModal } from './components/PhoneKeypadModal';
import { ConsentModal } from './components/ConsentModal';
import { ConferenceParticipantsView } from './components/ConferenceParticipantsView';
import { InfoField } from '../../components/InfoField';

interface PhoneActionsTabProps {
  caseInfo: SupportCaseInfoResponse['supportCase'];
  applicantInfo?: any;
  disabled?: boolean;
}

export const PhoneActionsTab: FC<PhoneActionsTabProps> = ({ caseInfo, applicantInfo, disabled }) => {
  const L = useLocalizedStrings();
  const { toast } = useToast();
  const context = useContext(InterfaceContext);
  const loggedInConfig = useContext(LoggedInConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);
  const isPhone = isPhoneContact(caseInfo.contact);

  // Get Twilio context
  const {
    twilioDevice,
    twilioCall,
    twilioCaller,
    setTwilioCall,
    twilioVoiceChannel,
    resetTwilioVoiceChannel
  } = useContext(TwilioDeviceContext);

  // State for modals
  const [showCallConfirmation, setShowCallConfirmation] = useState<'call' | 'conference' | null>(null);

  // Determine phone number and contact name
  const phoneNumber = isPhone ? caseInfo.contact : '';
  const contactName = applicantInfo?.['legal_name'] || 'Applicant';

  // Get consent configuration
  const requireConsentToRecordCalls = loggedInConfig?.comms?.requireConsentToRecordCalls;
  const customConsentScript = requireConsentToRecordCalls?.customConsentScript
    ? (requireConsentToRecordCalls.customConsentScript as any)[context.lang] || requireConsentToRecordCalls.customConsentScript?.['en']
    : undefined;

  // Get conference configuration
  const translationService = publicConfig?.interface?.translationService;
  const conferenceNumbers = publicConfig?.interface?.conferenceNumbers || [];

  // Use call manager hook
  const {
    callState,
    activeCallInfo,
    showKeypad,
    setShowKeypad,
    startCall,
    startConferenceCall,
    hangup,
    toggleMute,
    sendDigit,
    showingConsentBox,
    setShowingConsentBox,
    callAnswered,
    consentGranted,
    setConsentGranted,
    isRecording,
    callInProgress,
    recordCall,
    resetConsentState,
    saveUnrecordedOutboundCall,
    // Conference participant state
    applicantCallSid,
    translatorCallSid,
    conferenceNumbersSids,
    conferenceNumberStatuses,
    conferenceNumbersLoading,
    applicantConfStatus,
    translatorConfStatus,
    applicantLoading,
    translatorLoading,
    addParticipantToConference,
    removeParticipantFromConference
  } = useCallManager(phoneNumber, contactName, twilioVoiceChannel, requireConsentToRecordCalls);

  // Handle call button click
  const handleCallButtonClick = () => {
    // If consent is required, show consent notice first
    if (requireConsentToRecordCalls) {
      setShowingConsentBox('notice');
      return;
    }
    // Otherwise show confirmation modal
    setShowCallConfirmation('call');
  };

  // Handle conference button click
  const handleConferenceButtonClick = () => {
    setShowCallConfirmation('conference');
  };

  // Handle starting call from consent notice
  const handleStartCallFromConsent = async () => {
    try {
      setShowingConsentBox('script');
      await startCall(twilioDevice, twilioCaller, twilioCall, setTwilioCall);
    } catch (error: any) {
      console.error('Error starting call:', error);
      toast({
        description: error.message || L.applicant.comms.call_failed,
        variant: 'error'
      });
      setShowingConsentBox(null);
    }
  };

  // Handle call confirmation (for non-consent flow)
  const handleConfirmCall = async () => {
    try {
      if (showCallConfirmation === 'call') {
        await startCall(twilioDevice, twilioCaller, twilioCall, setTwilioCall);
      } else if (showCallConfirmation === 'conference') {
        await startConferenceCall(twilioDevice, twilioCaller, twilioCall, setTwilioCall);
      }
      setShowCallConfirmation(null);
    } catch (error: any) {
      console.error('Error starting call:', error);
      toast({
        description: error.message || L.applicant.comms.call_failed,
        variant: 'error'
      });
      setShowCallConfirmation(null);
    }
  };

  // Handle consent granted
  const handleConsentGranted = async () => {
    setConsentGranted(true);
    setShowingConsentBox(null);
    if (twilioCall) {
      await recordCall(twilioCall);
    }
  };

  // Handle consent denied
  const handleConsentDenied = async () => {
    if (twilioCall) {
      // Save unrecorded call info when consent is explicitly denied
      await saveUnrecordedOutboundCall({
        callAgent: twilioCall.customParameters.get('agent'),
        callSid: twilioCall.parameters.CallSid,
        to: phoneNumber,
        channel: twilioVoiceChannel,
        status: 'consent_denied'
      });
      setConsentGranted(false);
      setShowingConsentBox(null);
    }
  };

  // Handle hangup
  const handleHangup = () => {
    hangup(twilioCall);
  };

  // Handle toggle mute
  const handleToggleMute = () => {
    toggleMute(twilioCall);
  };

  // Handle digit press from keypad
  const handleDigit = (digit: string) => {
    sendDigit(twilioCall, digit);
  };

  // Determine if we can make calls
  const canMakeCalls = isPhone && twilioDevice && twilioVoiceChannel === caseInfo.channel;

  return (
    <div className="flex flex-col space-y-6 overflow-y-auto h-full">
      {/* Voice Channel Mismatch Warning */}
      {twilioVoiceChannel !== caseInfo.channel && (
        <Alert variant="warning">
          {L.applicant.comms.chanel_is_not_voice_enabled}{' '}
          <a
            className="text-indigo-600 hover:text-indigo-800 underline cursor-pointer"
            onClick={async () => caseInfo.channel && await resetTwilioVoiceChannel(caseInfo.channel)}
          >
            {L.applicant.comms.click_here}
          </a>
        </Alert>
      )}

      {/* Phone Actions Section */}
      {isPhone && (
        <div className="pb-6 border-b border-gray-200 min-w-0">
          <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{L.applicant.comms.phone_actions}</h3>

          {/* Call Buttons */}
          <div className="flex flex-col gap-3 min-w-0 items-center">
            <Button
              variant="primary"
              className="w-full"
              icon={<Icon name="PhoneIcon" className="h-3 w-3" />}
              iconPlacement="left"
              onClick={handleCallButtonClick}
              isDisabled={disabled || !canMakeCalls || showingConsentBox === 'notice' || callInProgress || callState !== "idle"}
            >
              {callState === "active" ? L.applicant.comms.call_in_progress : L.applicant.comms.call}
            </Button>
            <Button
              variant="secondary"
              className="w-full"
              icon={<Icon name="PhoneIcon" className="h-3 w-3" />}
              iconPlacement="left"
              onClick={handleConferenceButtonClick}
              isDisabled={disabled || !canMakeCalls || showingConsentBox === 'notice' || callInProgress || callState !== "idle"}
            >
              {callState === "conference"
                ? L.applicant.comms.conference_in_progress
                : L.applicant.comms.start_conference}
            </Button>
            {/* Starting Call */}
            {callState === "starting" && <div className="text-sm text-gray-500">{L.applicant.comms.starting_call}</div>}
            {/* Call In Progress Error */}
            {callInProgress && !twilioCall && (
              <div className="text-xs text-red-700">
                {L.applicant.comms.existing_call_in_progress}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Consent Modal */}
      {showingConsentBox && (
        <ConsentModal
          showingConsentBox={showingConsentBox}
          onStartCall={handleStartCallFromConsent}
          onCancel={() => setShowingConsentBox(null)}
          onConsentGranted={handleConsentGranted}
          onConsentDenied={handleConsentDenied}
          callAnswered={callAnswered}
          customConsentScript={customConsentScript}
        />
      )}

      {/* Active Call View */}
      {(callState === "active" || callState === "conference") && activeCallInfo && (
        <div className="pb-6 border-b border-gray-200 min-w-0">
          <ActiveCallView
            activeCallInfo={activeCallInfo}
            onShowKeypad={() => setShowKeypad(true)}
            onHangup={handleHangup}
            onToggleMute={handleToggleMute}
            requireConsentToRecordCalls={requireConsentToRecordCalls}
            consentGranted={consentGranted}
            isRecording={isRecording}
          />
        </div>
      )}

      {/* Conference Participants View */}
      {callState === "conference" && (translationService || conferenceNumbers.length > 0) && (
        <div className="pb-6 border-b border-gray-200 min-w-0">
          <ConferenceParticipantsView
            applicantCallSid={applicantCallSid}
            applicantLoading={applicantLoading}
            applicantConfStatus={applicantConfStatus}
            onAddApplicant={() => addParticipantToConference('applicant')}
            onRemoveApplicant={() => removeParticipantFromConference('applicant', twilioCall)}
            translatorCallSid={translatorCallSid}
            translatorLoading={translatorLoading}
            translatorConfStatus={translatorConfStatus}
            onAddTranslator={() => addParticipantToConference('translator')}
            onRemoveTranslator={() => removeParticipantFromConference('translator', twilioCall)}
            showTranslator={!!translationService}
            conferenceNumbers={conferenceNumbers}
            conferenceNumbersSids={conferenceNumbersSids}
            conferenceNumbersLoading={conferenceNumbersLoading}
            conferenceNumberStatuses={conferenceNumberStatuses}
            onAddConferenceNumber={(phoneNumber, dialCode) =>
              addParticipantToConference('conferenceNumber', phoneNumber, dialCode)
            }
            onRemoveConferenceNumber={(phoneNumber) =>
              removeParticipantFromConference('conferenceNumber', twilioCall, phoneNumber)
            }
            currentLang={context.lang as string}
          />
        </div>
      )}

      {/* Contact Information */}
      <div className="min-w-0">
        <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">{L.applicant.comms.contact_information}</h3>
        <div className="space-y-3 min-w-0">
          <InfoField label={L.support.phone_number} value={applicantInfo?.phone_number || L.support.na} />
          <InfoField label={L.support.email} value={applicantInfo?.email || L.support.na} />
        </div>
      </div>

      {/* Call Confirmation Modal */}
      <CallConfirmationModal
        open={showCallConfirmation !== null}
        onClose={() => setShowCallConfirmation(null)}
        onConfirm={handleConfirmCall}
        contactName={contactName}
        phoneNumber={phoneNumber}
      />

      {/* Phone Keypad Modal */}
      <PhoneKeypadModal open={showKeypad} onClose={() => setShowKeypad(false)} onDigit={handleDigit} />
    </div>
  );
};